/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo, useState } from "react";
import { Box, Group, Radio, SimpleGrid, Stack } from "@mantine/core";

import {
  Button,
  DECIMAL_PROPS,
  InputLabel,
  NumberInput,
  Select,
  Text,
  Textarea,
  YesNoRadioButton,
} from "components";
import { CreditRequestStep, CreditResponse, ValidationRules } from "types";
import { AppConstants } from "../../../../constants";
import CreditRequestExpenses from "./outcome/expense";
import { AppUtils } from "utils";

const { CREDIT_REQUEST_FORM_PADDING } = AppConstants;

const OutcomeInformationStep = ({
  form,
  isEditable,
  onSubmit,
  options,
  t,
}: CreditRequestStep) => {
  const onSubmitWrapper = useCallback(() => {
    if (isEditable && !isFormValid()) {
      return;
    }
    onSubmit();
  }, [isEditable, form.values, options]);

  const isFormValid = useCallback(() => {
    let isValid = true;
    const runValidation = (name: keyof CreditResponse | any, value: any, rules: ValidationRules) => {
      const result = AppUtils.validateField(name, value, rules, form, t);
      if (!result) {
        isValid = false;
      }
    };
    if (form.values.mortgage) {
      runValidation('mortgageAmount', form.values.mortgageAmount, { required: true })
    }
    if (form.values.rent) {
      runValidation('rentAmount', form.values.rentAmount, { required: true })
    }
    return isValid;
  }, [form.values, options]);

  const [mortgageOrRent, setMortgageOrRent] = useState<'MORTGAGE' | 'RENT' | undefined>(form.getInputProps('mortgage').value ? 'MORTGAGE' : form.getInputProps('rent').value ? 'RENT' : undefined);

  const changeMortgageOrRent = useCallback((val: 'MORTGAGE' | 'RENT') => {
    setMortgageOrRent(val);
    if (val === 'MORTGAGE') {
      form.setFieldValue('mortgage', true);
      form.setFieldValue('rent', undefined);
      form.setFieldValue('rentAmount', undefined);
    } else if (val === 'RENT') {
      form.setFieldValue('rent', true);
      form.setFieldValue('mortgage', undefined);
      form.setFieldValue('mortgageAmount', undefined);
    }
  }, []);

  const houseSituationOptions = useMemo(() => {
    const { maritalStatus } = form.values;
    const exceptions: string[] = [];
    if (maritalStatus === 'SINGLE' || maritalStatus === 'DIVORCED' || maritalStatus === 'WIDOWED') {
      exceptions.push('SPOUSE');
    }
    if (maritalStatus === 'MARRIED') {
      exceptions.push('ROOMMATES');
    }
    return options['houseSituation']
      .filter(el => !exceptions.includes(el))
      .map(el => (
        { value: el, label: t(`input.houseSituation.options.${el}`) }
      ));
  }, [form, options])

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }}>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING} mt={'md'}>
        <Select
          label={t('input.houseSituation.label')}
          data={houseSituationOptions}
          key={form.key('houseSituation')}
          {...form.getInputProps('houseSituation')}
          clearable={true}
          disabled={!isEditable}
        />
        <Box mb={6}>
          <InputLabel label={t('input.mortgageOrRent.label')} required={true} />
          <Group>
            <Radio color={'#75AD53'} checked={mortgageOrRent === 'MORTGAGE'} onChange={() => changeMortgageOrRent('MORTGAGE')} label={t('input.mortgageOrRent.options.MORTGAGE')} disabled={!isEditable} />
            <Radio color={'#75AD53'} checked={mortgageOrRent === 'RENT'} onChange={() => changeMortgageOrRent('RENT')} label={t('input.mortgageOrRent.options.RENT')} disabled={!isEditable} />
          </Group>
        </Box>
        {mortgageOrRent === 'MORTGAGE' ? (
          <NumberInput
            label={t('input.mortgageAmount.label')}
            key={form.key('mortgageAmount')}
            {...form.getInputProps('mortgageAmount')}
            min={0}
            rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
            rightSectionWidth={40}
            disabled={!isEditable}
            required={true}
            error={form.errors.mortgageAmount}
            {...DECIMAL_PROPS}
          />
        ) : undefined}
        {mortgageOrRent === 'RENT' ? (
          <NumberInput
            label={t('input.rentAmount.label')}
            key={form.key('rentAmount')}
            {...form.getInputProps('rentAmount')}
            min={options['rentAmount']?.min}
            max={options['rentAmount']?.max}
            rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
            rightSectionWidth={40}
            disabled={!isEditable}
            required={true}
            error={form.errors.mortgageAmount}
            {...DECIMAL_PROPS}
          />
        ): undefined}
        <NumberInput
          label={t('input.healthInsuranceAmount.label')}
          key={form.key('healthInsuranceAmount')}
          {...form.getInputProps('healthInsuranceAmount')}
          rightSection={<Text size={'sm'} >{t('currency.switzerland')}</Text>}
          rightSectionWidth={40}
          disabled={!isEditable}
          {...DECIMAL_PROPS}
        />
        <YesNoRadioButton
          label={t('input.healthInsuranceDiscount.label')}
          name={'healthInsuranceDiscount'}
          form={form}
          t={t}
          disabled={!isEditable}
        />
        <YesNoRadioButton
          label={t('input.enforcedCollection.label')}
          name={'enforcedCollection'}
          form={form}
          t={t}
          disabled={!isEditable}
        />
      </Stack>
      <Stack pb={CREDIT_REQUEST_FORM_PADDING} px={CREDIT_REQUEST_FORM_PADDING}>
        <CreditRequestExpenses form={form} options={options} t={t} disabled={!isEditable} />
        <Textarea
          label={t('input.comment.label')}
          key={form.key('comment3')}
          {...form.getInputProps('comment3')}
          info={t('input.comment.info.message')}
          disabled={!isEditable}
        />
        <Button onClick={onSubmitWrapper}>
          {t(`action.${isEditable ? 'submitRequest' : 'goToDocumentation'}`)}
        </Button>
      </Stack>
    </SimpleGrid>
  )
}

export default OutcomeInformationStep;